'use client';

import {
  type ElementType,
  type ReactElement,
  cloneElement,
  forwardRef,
  isValidElement,
  useContext,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { BannerRootContext } from './context/banner-root.context';
import { bannerCloseStyles } from './styles/banner-close.styles';
import type { BannerClosePropsInterface } from './types/banner-close.types';

/**
 * @since 10.0.0
 */

export const BannerClose = forwardRef<HTMLDivElement, BannerClosePropsInterface>(
  (props, forwardedRef) => {
    const { onClose, closeBanner } = useContext(BannerRootContext);
    const { asChild = false, className, children, css: cssProp, ...rest } = props;

    const styles = css(bannerCloseStyles, cssProp);

    const customOnClose = () => {
      closeBanner();
      if (onClose) {
        onClose();
      }
    };

    let closeContent = children;
    if (isValidElement(children)) {
      closeContent = cloneElement(children as ReactElement, { onClick: customOnClose });
    }

    const Component: ElementType = asChild ? Slot : 'div';

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="Banner.Close"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_banner__close', styles, className)}
        {...rest}
      >
        {closeContent}
      </Component>
    );
  },
);

BannerClose.displayName = 'Banner.Close';
